import {useShallow} from 'zustand/react/shallow';
import type {DragEndEvent, DragStartEvent} from '@dnd-kit/core';
import DndContextWrapper from 'client/components/QuestionTypes/shared/DndContextWrapper';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import type {GuessModelV1} from '@albert-io/models';
import {ItemBankRefsContext} from './ItemBankRefsContext';
import {SupplementChip} from 'client/components/QuestionTypes/shared/DraggableChip';
import {createPortal} from 'react-dom';
import {WORD_BANK_ID} from '../store';
import WordBank from './WordBank.react';
import useQuestionAndDrafts from '../../V2/useQuestionAndDrafts.hook';
import DropSlots from './DropSlots.react';
import useQuestionStore from 'client/components/QuestionTypes/V2/useQuestionStores';

const ANIMATION_DURATION = 420;

interface FlyingChipProps {
  value: string;
  fromRect: DOMRect;
  toRect: DOMRect;
}

const FlyingChip = ({value, fromRect, toRect}: FlyingChipProps) => {
  const dx = toRect.left + toRect.width / 2 - (fromRect.left + fromRect.width / 2);
  const dy = toRect.top + toRect.height / 2 - (fromRect.top + fromRect.height / 2);

  const [style, setStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    top: fromRect.top,
    left: fromRect.left,
    width: fromRect.width,
    pointerEvents: 'none',
    zIndex: 9999,
    margin: 0,
    transform: 'translate(0, 0) scale(1.07)',
    transformOrigin: 'center center',
    transition: 'none'
  });

  useEffect(() => {
    // first frame paints the initial state, second starts the transition
    const frame1 = requestAnimationFrame(() => {
      const frame2 = requestAnimationFrame(() => {
        setStyle((s) => ({
          ...s,
          transition: `transform ${ANIMATION_DURATION}ms cubic-bezier(0.22, 1, 0.36, 1)`,
          transform: `translate(${dx}px, ${dy}px) scale(1)`
        }));
      });
      return () => cancelAnimationFrame(frame2);
    });
    return () => cancelAnimationFrame(frame1);
  }, [dx, dy]);

  return createPortal(
    <div style={style}>
      <SupplementChip value={value} size='small' />
    </div>,
    document.body
  );
};
interface Props {
  question: Record<string, any>;
  isGuessSubmitted?: boolean;
  isGuessCorrect?: boolean;
  guess?: GuessModelV1 | null;
  // storeName
}

export const CommonBankQuestionArea = ({question, isGuessSubmitted, guess}: Props) => {
  const {initialize, saveDraftGuess, addGuessInfo} = useQuestionAndDrafts(question);
  const useStoreHook = useQuestionStore<'item-bank'>(question);
  const [flyingItem, setFlyingItem] = useState<FlyingChipProps | null>(null);
  const flyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const itemElsRef = useRef<Map<string, HTMLElement>>(new Map());
  const slotElsRef = useRef<Map<string, HTMLElement>>(new Map());

  const registerItemEl = useCallback((id: string, el: HTMLElement | null) => {
    if (el) itemElsRef.current.set(id, el);
    else itemElsRef.current.delete(id);
  }, []);

  const registerSlotEl = useCallback((id: string, el: HTMLElement | null) => {
    if (el) slotElsRef.current.set(id, el);
    else slotElsRef.current.delete(id);
  }, []);

  const {
    questionInStore,
    wordBankItems,
    placeItemInSlot,
    setActiveItem,
    activeItemId,
    isDragging,
    setIsDragging,
    isSubmitted
    // oxlint-disable-next-line react/react-compiler -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  } = useStoreHook(
    useShallow((state) => ({
      questionInStore: state.question,
      wordBankItems: state.wordBank,
      placeItemInSlot: state.placeItemInSlot,
      activeItemId: state.activeItemId,
      setActiveItem: state.setActiveItem,
      isDragging: state.isDragging,
      setIsDragging: state.setIsDragging,
      isSubmitted: state.isSubmitted
    }))
  );

  const onDragStart = (_event: DragStartEvent) => {
    setIsDragging(true);
    setActiveItem(null); // Clear click-to-move selection when dragging starts
  };

  const onDrop = (event: DragEndEvent) => {
    const {active, over} = event;
    setIsDragging(false);

    if (!over || active.id === over.id) return;
    placeItemInSlot(active.id as string, over.id as string);
    saveDraftGuess();
  };

  const handleItemClick = useCallback(
    (itemId: string) => {
      if (isSubmitted) return;
      // Don't process clicks during drag operations
      if (isDragging) return;

      // If clicking the already-selected item, deselect it
      if (activeItemId === itemId) {
        setActiveItem(null);
      } else if (activeItemId) {
        // If another item is selected, move the active item to where the clicked item is
        // The clicked item will be sent back to the word bank
        const clickedItem = wordBankItems.find((item) => item.id === itemId);

        if (clickedItem) {
          // Move active item to clicked item's location (which will displace the clicked item)
          placeItemInSlot(activeItemId, clickedItem.placedInSlot || WORD_BANK_ID);
          saveDraftGuess();
        }
        setActiveItem(null);
      } else {
        // Select this item for click-to-move
        setActiveItem(itemId);
      }
    },
    [
      wordBankItems,
      setActiveItem,
      isDragging,
      placeItemInSlot,
      activeItemId,
      saveDraftGuess,
      isSubmitted
    ]
  );

  const handleSlotClick = useCallback(
    (slotId: string) => {
      if (isSubmitted) return;
      if (isDragging) return;

      if (!activeItemId) return;

      // Measure before committing so the fly starts from the item's current on-screen spot.
      const sourceEl = itemElsRef.current.get(activeItemId) ?? null;
      const destEl = slotElsRef.current.get(slotId) ?? null;
      const activeItem = wordBankItems.find((item) => item.id === activeItemId);
      const fly =
        sourceEl && destEl && activeItem
          ? {
              value: activeItem.value,
              fromRect: sourceEl.getBoundingClientRect(),
              toRect: destEl.getBoundingClientRect()
            }
          : null;

      // Commit the placement immediately so a submit or navigation during the animation
      // reflects what the student sees; the fly is a cosmetic flourish over the placed item.
      placeItemInSlot(activeItemId, slotId);
      setActiveItem(null);
      saveDraftGuess();

      if (fly) {
        setFlyingItem(fly);
        if (flyTimeoutRef.current) clearTimeout(flyTimeoutRef.current);
        flyTimeoutRef.current = setTimeout(() => {
          setFlyingItem(null);
          flyTimeoutRef.current = null;
        }, ANIMATION_DURATION);
      }
    },
    [
      activeItemId,
      wordBankItems,
      placeItemInSlot,
      setActiveItem,
      isDragging,
      saveDraftGuess,
      isSubmitted
    ]
  );

  const handleBankClick = useCallback(() => {
    handleSlotClick(WORD_BANK_ID);
  }, [handleSlotClick]);

  // Reset (unplace) an item back to the word bank
  const handleResetItem = useCallback(
    (itemId: string) => {
      if (isSubmitted) return;
      placeItemInSlot(itemId, WORD_BANK_ID);
      setActiveItem(null);
      saveDraftGuess();
    },
    [isSubmitted, placeItemInSlot, setActiveItem, saveDraftGuess]
  );

  useEffect(() => {
    if (question && question.size > 0) {
      if (isGuessSubmitted && guess) {
        addGuessInfo(guess);
      } else {
        // Explicitly clear any stale submission state from a previously viewed question
        addGuessInfo(null);
      }
      initialize(question);
    }
  }, [question, initialize, isGuessSubmitted, guess, addGuessInfo]);

  useEffect(
    () => () => {
      if (flyTimeoutRef.current) clearTimeout(flyTimeoutRef.current);
    },
    []
  );

  if (!questionInStore) return null;

  return (
    <ItemBankRefsContext.Provider value={{registerItemEl, registerSlotEl}}>
      <DndContextWrapper
        onDragStart={onDragStart}
        onDragEnd={onDrop}
        renderOverlay={(activeId) => {
          const item = wordBankItems.find((i) => i.id === activeId);
          return item ? <SupplementChip value={item.value} size='small' /> : null;
        }}
      >
        {flyingItem && <FlyingChip {...flyingItem} />}
        <div className='mb-x4'>
          <WordBank
            handleItemClick={handleItemClick}
            question={question}
            handleBankClick={handleBankClick}
          />
          <DropSlots
            question={question}
            handleItemClick={handleItemClick}
            handleSlotClick={handleSlotClick}
            handleResetItem={handleResetItem}
          />
        </div>
      </DndContextWrapper>
    </ItemBankRefsContext.Provider>
  );
};

export default CommonBankQuestionArea;
